import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import MuiDialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CircularProgress, styled } from '@mui/material';


import { MessageTypeConstants } from '../../../../services/bot/Message';
import { getAuthData, getFromLocalStorage } from '../../../../services/storage/LocalStorage';
import { UPDATE_NONCONVERSATIONAL_FILTERS } from '../../../../state/constants';
import { renderNonconversationalFilter, renderNonconversationalFilterFlags } from '../../../../state/actions/filters';
import { processSendMessage } from '../../../../services/bot/conversation/SendMessage';
import { showToast } from '../../../ui/toaster';

import FMFilterFieldsComponent from './FMFilterFields';
import store from '../../../../state/configureStore';
import { setButtonLoader } from '../../../../state/actions/loader';

const CustomizedTypography = styled(Typography)({ fontWeight: 'bold' });
const CustomizedCloseIcon = styled(CloseIcon)(({ theme }) => ({ color: theme.palette.grey[500] }));
const DialogContentStyle = styled(DialogContent)(() => ({ padding: '20px 24px !important' }));
const DialogActionsStyle = styled(DialogActions)(() => ({ padding: '20px 24px !important' }));
const CustomizedInnerGrid = styled(Grid)({ display: 'inline-flex' });


const MainButton = styled(Button)(() => ({
  border: 'none',
  padding: '7px 45px',
  borderRadius: 20,
  textTransform: 'inherit',
  '&:hover': { border: 'none' },
}));

const CancelButton = styled(MainButton)(() => ({
  backgroundColor: '#e0e8ff',
  color: '#638dff',
  border: 'none',
  fontFamily: 'SF Pro Text Bold',
  fontSize: 14,
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#eef2ff' },
  '&:focus': {
    boxShadow: '0 0 6px 0 rgba(99, 141, 255, 0.6)',
    backgroundColor: '#eef2ff',
  },
  '&:disabled': {
    backgroundColor: '#e8ecf8',
    color: '#aeb8d6',
    border: 'none',
  },
}));

const ConfirmButton = styled(MainButton)(() => ({
  backgroundColor: '#638dff',
  color: '#FFFFFF',
  fontFamily: 'SF Pro Text Bold',
  fontSize: 14,
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#4c71d6' },
  '&:focus': { boxShadow: '0 0 6px 0 #638dff !important' },
  '&:disabled': {
    backgroundColor: '#aeb8d6',
    color: '#e8ecf8',
  },
}));

const CustomizedParameterButton = styled(Button)({
  textTransform: 'none',
  borderRadius: 4,
  padding: '6px 25px',
  backgroundColor: '#638dff',
  color: '#FFFFFF',
  fontFamily: 'SF Pro Text Bold',
  fontSize: 14,
  boxShadow: 'none',
  width: '47%',
  // height: "30%",
  '&:hover': { backgroundColor: '#4c71d6' },
  '&:focus': { boxShadow: '0 0 6px 0 #638dff !important' },
  '&:disabled': {
    backgroundColor: '#aeb8d6',
    color: '#e8ecf8',
  },
});

const CustomizedGrid = styled(Grid)({
});

const CustomizedSaveFilterGrid = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
});

const CustomizedParamTextGrid = styled(Grid)({
  textAlign: 'center',
  marginBottom: 10,
  color: '#645622',
});

const CustomizedTextField = styled(TextField)({
  '& div': {
    backgroundColor: 'white',
    borderRadius: '8px',
    border: 'none',
    height: '40px',
  },
});

const CustomizedSaveButton = styled(MainButton)({
  margin: 3,
  marginLeft: 10,
  height: 'fit-content',
  alignSelf: 'center',
  padding: '7px 22px',
  backgroundColor: '#638dff',
  color: '#FFFFFF',
  fontFamily: 'SF Pro Text Bold',
  fontSize: 14,
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#4c71d6' },
  '&:focus': { boxShadow: '0 0 6px 0 #638dff !important' },
  '&:disabled': {
    backgroundColor: '#aeb8d6',
    color: '#e8ecf8',
  },
});

const DialogTitle = ({ children, onClose, ...other }) => (
  <MuiDialogTitle
    sx={() => ({ margin: 0 })}
    disableTypography
    {...other}>
    <Typography variant='h6'>{children}</Typography>
    {onClose ? (
      <IconButton
        sx={(theme) => ({
          position: 'absolute',
          right: theme.spacing(0),
          color: theme.palette.grey[500],
          zIndex: 1,
          top: '10px',
          paddingTop: '5px',
        })}
        aria-label='close'
        onClick={onClose}
        size='large'>
        <CustomizedCloseIcon />
      </IconButton>
    ) : null}
  </MuiDialogTitle>
);


const FMFilterFormComponent = ({ conversation }) => {
  const dispatch = useDispatch();
  const { user: { userTimezone } } = getAuthData();
  const defaultUserTimezone = 'Etc/UTC';

  const NonConversationalFilter = useSelector((state) => state.filters);
  const [filterName, setFilterName] = useState(NonConversationalFilter?.activeFilterName || '');
  const [editFilter, setEditFilter] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [newFilterDialog, setNewFilterDialog] = useState(false);
  const [avaiableFilterNames] = useState(NonConversationalFilter?.availableFilters?.map((filter) => filter.name));
  const [newFilterErrorMessage, setNewFilterErrorMessage] = useState(null);
  const [renameFilterNameErrorMsg, setRenameFilterNameErrorMsg] = useState(null);

  const [applyFilterState, setApplyFilterState] = useState({ state: false, type: "" });
  const previousActiveFilter = getFromLocalStorage("previousActiveNonConversationFilter");
  const buttonLoader = useSelector((state) => state.loader.buttonLoader);

  //handle button loader
  useEffect(() => {
    if (buttonLoader === false && applyFilterState.state) {
      updateReduxFlags({ openDrawer: false });
      setApplyFilterState({ state: false, type: "" });
    }
  }, [buttonLoader]);

  useEffect(() => {
    if (NonConversationalFilter?.isNewFilter) {
      setEditFilter(true);
    }
  }, [NonConversationalFilter?.isNewFilter]);

  const handleFilterNameChange = (e) => {
    setFilterName(e.target.value);
    if (avaiableFilterNames?.includes(e.target.value)) {
      setRenameFilterNameErrorMsg('Filter name already been used.');
    } else {
      setRenameFilterNameErrorMsg(null);
    }
  };

  const handleFormAction = useCallback(
    (action, currentField = null, currentFieldValue = null) => {
      const responseChat = {
        messageType: MessageTypeConstants.MESSAGE_TYPE_TABLE_RESPONSE,
        message: {
          controlId: NonConversationalFilter?.controlId,
          tabId: NonConversationalFilter?.parent
            ? NonConversationalFilter?.parentTabId
            : NonConversationalFilter?.tabId,
          action,
          currentField,
          content: { currentFieldValue },
          tz: userTimezone || defaultUserTimezone,
        },
        ...(action !== "click" && { options: NonConversationalFilter || {} }),
      };

      processSendMessage(responseChat, true);
    }, [processSendMessage]);


  const updateRedux = useCallback(
    (field) => {
      const formMessage = {
        message: [],
        options: {
          action: "changeFilter",
          activeFilterName: NonConversationalFilter?.activeFilterName,
          controlId: NonConversationalFilter?.controlId,
          formId: NonConversationalFilter?.formId,
          tabId: NonConversationalFilter?.parent
            ? NonConversationalFilter?.parentTabId
            : NonConversationalFilter?.tabId,
          parent: NonConversationalFilter?.parent,
          filteredColumns: { update: [field] },
        },
      };

      dispatch(
        renderNonconversationalFilter(
          formMessage,
          store.getState().filters?.filteredColumns
        )
      );
    },
    [dispatch, renderNonconversationalFilter, NonConversationalFilter]
  );


  const handleAction = useCallback(
    (action, activeFilterName, filteredColumns = null, applyFilter) => {
      const name = activeFilterName?.trim();
      if (!name) {
        showToast("error", "Preset name cannot be empty.");
        return;
      }

      const responseChat = {
        messageType: MessageTypeConstants.MESSAGE_TYPE_TABLE_RESPONSE,
        message: {
          controlId: NonConversationalFilter?.controlId,
          tabId: NonConversationalFilter?.parent
            ? NonConversationalFilter?.parentTabId
            : NonConversationalFilter?.tabId,
          action,
          tz: userTimezone || defaultUserTimezone,
          content: {
            activeFilterName: name,
            applyFilter,
            ...(filteredColumns !== null && {
              field: store.getState().filters?.filteredColumns ?? [],
            }),
          },
        },
        options: NonConversationalFilter || {},
      };

      processSendMessage(responseChat, true);
    },
    [processSendMessage, NonConversationalFilter]
  );


  const updateReduxFlags = useCallback((localFilterFlag) => {
    dispatch(
      renderNonconversationalFilterFlags({
        ...NonConversationalFilter,
        ...localFilterFlag,
      }),
    );
  }, []);


  const saveFilter = () => {
    const responseChat = {};
    responseChat.messageType = MessageTypeConstants.MESSAGE_TYPE_TABLE_RESPONSE;
    responseChat.message = {
      controlId: NonConversationalFilter.controlId,
      tabId: NonConversationalFilter.parent
        ? NonConversationalFilter.parentTabId
        : NonConversationalFilter.tabId,
      action: 'renameFilter',
      tz: userTimezone || defaultUserTimezone,
      content: {
        activeFilterName: NonConversationalFilter.activeFilterName || '',
        newFilterName: filterName,
      },
    };

    responseChat.options = NonConversationalFilter || {};
    processSendMessage(responseChat, true);
    setEditFilter(false);
    dispatch(
      renderNonconversationalFilterFlags({
        ...NonConversationalFilter,
        activeFilterName: filterName,
      }),
    );
  };

  const handleNewFilterCreation = () => {
    if (!filterName || filterName.trim() === "") {
      showToast('error', 'Preset name cannot be empty.');
      return;
    }

    handleAction('filter', filterName, store.getState().filters.filteredColumns, false);
    setNewFilterName('');
    dispatch(setButtonLoader(true));
    setApplyFilterState({ state: true, type: "save" });
    dispatch({ type: UPDATE_NONCONVERSATIONAL_FILTERS, data: { clearActiveFilterName: true } });
  };

  const handleNewFilterNameChange = (e) => {
    setNewFilterName(e.target.value);
    if (avaiableFilterNames.includes(e.target.value)) {
      setNewFilterErrorMessage('Filter name already been used.');
    } else {
      setNewFilterErrorMessage(null);
    }
  };

  const closeNewFilterDialog = () => {
    setNewFilterDialog(false);
    setNewFilterName('');
    setNewFilterErrorMessage(null);
  };


  const onSaveAndApplyNewFilter = () => {
    const activeFilterName = (filterName || NonConversationalFilter?.activeFilterName)?.trim();

    if (!activeFilterName || activeFilterName.trim() === "") {
      showToast('error', 'Preset name cannot be empty.');
      return;
    }

    handleAction(
      'filter',
      activeFilterName,
      NonConversationalFilter?.filteredColumns ?? [],
      true
    );

    setApplyFilterState({ state: true, type: "saveAndApply" });
    dispatch(setButtonLoader(true));
  };



  return (
    <>
      <DialogTitle
        id="customized-dialog-title"
        onClose={() => {
          const activeFilter =
            previousActiveFilter || NonConversationalFilter?.activeFilterName;

          handleAction("filter", activeFilter, NonConversationalFilter?.filteredColumns);
          updateReduxFlags({ openDrawer: false });
        }}
      />
      <DialogContent
        sx={(theme) => ({ padding: theme.spacing(2) })}
      >
        <div role='presentation' style={{ borderRadius: '10px' }}>
          <List subheader={<ListSubheader />}>
            <CustomizedTypography className='!text-size-14 text-center' variant='h5' gutterBottom>
              {NonConversationalFilter && NonConversationalFilter?.isNewFilter
                ? 'Create new filter'
                : 'Filter'}
            </CustomizedTypography>
            <CustomizedGrid container className='py-2'>
              <CustomizedInnerGrid item xs={12} className='w-full'>
                <CustomizedTextField
                  fullWidth
                  className='search-input'
                  disabled={!editFilter}
                  value={filterName}
                  sx={{
                    backgroundColor: "#fff",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#c4d8ff",
                      },
                      "&:hover fieldset": {
                        borderColor: "#92afff",
                      },
                      "&.Mui-focused fieldset": {
                        border: "1px solid #92afff",
                      },
                    },
                    "& input[type=text]": { height: 3 },
                  }}
                  placeholder='Filter Name'
                  onChange={handleFilterNameChange}
                  error={renameFilterNameErrorMsg !== null}
                  helperText={renameFilterNameErrorMsg}
                  style={{ borderRadius: '4px' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        {!editFilter ? (
                          <IconButton
                            aria-label='edit filter name'
                            onClick={() => setEditFilter(true)}
                            size='large'>
                            <BorderColorOutlinedIcon
                              style={{ color: '#638dff' }} />
                          </IconButton>
                        ) : filterName ? (
                          <IconButton
                            aria-label='edit filter name'
                            onClick={() => {
                              setEditFilter(false);
                              setFilterName(NonConversationalFilter.activeFilterName || '');
                              setRenameFilterNameErrorMsg(null);
                            }}
                            size='large'>
                            <CustomizedCloseIcon />
                          </IconButton>
                        ) : null}
                      </InputAdornment>
                    ),
                  }} />
                {editFilter && filterName && (
                  <CustomizedSaveButton
                    size='small'
                    onClick={saveFilter}
                    disabled={renameFilterNameErrorMsg !== null}>
                    Save
                  </CustomizedSaveButton>
                )}
              </CustomizedInnerGrid>
            </CustomizedGrid>
            <CustomizedTypography
              variant='h6'
              gutterBottom
              className='!text-size-14'
              style={{ marginTop: '4px' }}>
              Parameters
            </CustomizedTypography>
            <ListItem className='!px-0'>
              <FMFilterFieldsComponent
                conversation={conversation}
                handleAction={handleFormAction}
                updateRedux={updateRedux}
                showLargeScreen={false}
              />
            </ListItem>
            <ListItem className='!px-0'>
              {false && (
                <CustomizedParamTextGrid item xs={12}>
                  Some parameters have changed.
                </CustomizedParamTextGrid>
              )}
              <CustomizedSaveFilterGrid>
                <CustomizedParameterButton
                  className='!border !border-1 !border-gray-300 !text-gray-300'
                  onClick={handleNewFilterCreation}
                  style={{ backgroundColor: 'transparent', color: '#638dff', height: '5px', padding: '18px 8px', border: "1px solid gray" }}
                >
                  {buttonLoader && applyFilterState.state && applyFilterState.type == "save" ?
                    <CircularProgress size={14} sx={{ marginBlock: "5px", marginInline: "20px", color: "#2550FA" }} />
                    : "Save as a new filter"}
                </CustomizedParameterButton>
                <CustomizedParameterButton
                  // startIcon={<CheckIcon />}
                  onClick={() => onSaveAndApplyNewFilter()}
                  color='primary'
                  className='!bg-[#2550FA]'
                  disabled={renameFilterNameErrorMsg !== null}
                  style={{
                    height: '5px',
                    padding: '18px 8px',
                  }}>
                  {buttonLoader && applyFilterState.state && applyFilterState.type == "saveAndApply" ?
                    <CircularProgress size={14} sx={{ marginBlock: "5px", marginInline: "20px", color: "#ffff" }} />
                    : "Save and apply filter"}
                </CustomizedParameterButton>
              </CustomizedSaveFilterGrid>
            </ListItem>
          </List>
        </div>
      </DialogContent>
      <Dialog
        fullWidth
        maxWidth='sm'
        open={newFilterDialog}
        onClose={closeNewFilterDialog}
        aria-labelledby='form-dialog-title'>
        <DialogTitle id='form-dialog-title'>Save as new filter</DialogTitle>
        <DialogContentStyle>
          <TextField
            autoFocus
            id='name'
            label='New Filter Name'
            fullWidth
            variant='outlined'
            value={newFilterName}
            onChange={handleNewFilterNameChange}
            error={newFilterErrorMessage !== null}
            helperText={newFilterErrorMessage} />
        </DialogContentStyle>
        <DialogActionsStyle>
          <CancelButton
            variant='outlined'
            color='secondary'
            onClick={closeNewFilterDialog}>
            Cancel
          </CancelButton>
          <ConfirmButton
            variant='outlined'
            onClick={handleNewFilterCreation}
            color='primary'
            disabled={newFilterErrorMessage !== null}>
            Create
          </ConfirmButton>
        </DialogActionsStyle>
      </Dialog>
    </>
  );
};

export default React.memo(FMFilterFormComponent);
